import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getEmployeesByAdminAndOrg, getZone } from "services/getEmployees";
import { createDistributedTowerData } from "services/legalAwarenessService";
import ConfirmationModal from "components/ConfirmationModal1";
import { LoadingIcon } from "utils/constant";

interface WoredaData {
    uu_id: string;
    unit_name: string;
}

export default function DistributeToWoreda({ setFormState, formState, isOpen, onClose }: { setFormState: any, formState: any, isOpen?: boolean, onClose?: () => void }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const { showToast } = useToast();
    const [selectedWoredas, setSelectedWoredas] = useState<WoredaData[]>([]);
    const [woredas, setWoredas] = useState([]);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const contextData = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(isOpen || false);

    useEffect(() => {
        let cancelled = false;
        const adminUnit = contextData?.user?.admin_unit || "";
        const token = contextData.token ?? contextData?.user?.access_token ?? contextData?.user?.user?.access_token;

        if (!adminUnit) return;

        fetch(`${process.env.REACT_APP_API_BASE_URL_2}organization/administration-structures/child_units/${adminUnit}/`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
            .then((res) => res.json())
            .then((response: any) => {
                if (cancelled || !response) return;
                let woredasArray: any[] = [];
                if (response.sub_units && Array.isArray(response.sub_units)) {
                    woredasArray = response.sub_units;
                } else if (response.data && Array.isArray(response.data)) {
                    woredasArray = response.data;
                } else if (response.units && Array.isArray(response.units)) {
                    woredasArray = response.units;
                } else if (response.child_admin_units && response.child_admin_units.units && Array.isArray(response.child_admin_units.units)) {
                    woredasArray = response.child_admin_units.units;
                } else if (response.results && Array.isArray(response.results)) {
                    woredasArray = response.results;
                } else if (Array.isArray(response)) {
                    woredasArray = response;
                } else if (typeof response === "object") {
                    woredasArray = Object.values(response).filter(
                        (item) => item && typeof item === "object" && (item as any).unit_name
                    ) as any[];
                }
                setWoredas(woredasArray as any);
                if (woredasArray.length === 0) {
                    console.warn("No woredas found in response:", response);
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    console.error("Error fetching woredas:", error);
                    showToast(t("FailedToFetchZones"), "error");
                }
            });
        return () => { cancelled = true; };
    }, [contextData.token, contextData?.user?.access_token, contextData?.user?.admin_unit, showToast, t]);

    // Woredas this zone has already distributed to (by admin_unit id) — prevent re-distribution.
    const alreadyDistributed = formState?.distrbuted_to_woreda || formState?.distributed_to_woreda || [];
    const distributedAdminUnitSet = new Set(
        (alreadyDistributed as any[]).map((w: any) => String(w.admin_unit ?? w.admin_unit_id))
    );
    const isAlreadyDistributed = (woreda: any) => distributedAdminUnitSet.has(String(woreda.uu_id));

    const handleClose = () => {
        if (onClose) {
            onClose();
        } else {
            setIsModalOpen(false);
        }
    };

    const isModalOpenState = isOpen !== undefined ? isOpen : isModalOpen;

    const handleForward = async () => {
        if (selectedWoredas.length === 0) {
            showToast(t("PleaseSelectAtLeastOneWoreda"), "error");
            return;
        }

        setLoading(true);

        const token = contextData.token ?? contextData?.user?.access_token ?? contextData?.user?.user?.access_token;

        try {
            for (const woreda of selectedWoredas) {
                // Skip woredas already distributed to (defensive guard).
                if (isAlreadyDistributed(woreda)) continue;
                const woredaData = new FormData();
                woredaData.append("status", "Sent To woreda Office");
                woredaData.append("awareness", formState.id || "");
                woredaData.append("admin_unit", woreda.uu_id);
                woredaData.append("admin_unit_name", woreda.unit_name);
                
                woredaData.append("cordinator_id", contextData?.user?.user_id || "");
                woredaData.append("sent_date", new Date().toISOString().split("T")[0]);
                
                const doc = formState.documents?.find((document: any) => document.document_type === "distrbuted_zone_awarness")
                    || formState.documents?.find((document: any) => document.document_type === "legal_awareness_document_file");
                if (doc) {
                    const fileurl = `${process.env.REACT_APP_API_BASE_URL}legal_awareness/legal-awareness-documents/document/${doc.document_uuid}`;
                    const response = await fetch(fileurl, {
                        headers: token ? { Authorization: `Bearer ${token}` } : {},
                    });
                    const blob = await response.blob();
                    const file = new File([blob], doc.document_path || "file", {
                        type: blob.type,
                    });
                    woredaData.append("awarenes_files", file);
                }

                await createDistributedTowerData(woredaData);
            }
            showToast(t("DistributedToWoredasSuccess"), "success");
            
            setFormState((prev: any) => ({
                ...prev,
                distributed_to_woreda: [
                    ...(prev.distributed_to_woreda || []),
                    ...selectedWoredas.map(w => ({
                        admin_unit_name: w.unit_name,
                        admin_unit_id: w.uu_id,
                        sent_date: new Date().toISOString().split("T")[0],
                        status: "Sent To Woreda"
                    }))
                ]
            }));
            
            handleClose();
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToDistributeToWoredas"), "error");
        } finally {
            setLoading(false);
        }
    };

    if (!isModalOpenState) return null;

    return (
        <>
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-end p-4 z-50 overflow-hidden">
                <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-2xl w-full max-w-lg p-6 relative flex flex-col h-full my-auto overflow-y-auto pr-2 custom-scrollbar border border-gray-200">
                    <button
                        onClick={handleClose}
                        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>

                    <div className="flex items-center mb-6">
                        <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                            <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-gray-800">
                            {t("DistributeToWoreda")}
                        </h2>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-4">
                        {t("DistributeToWoredaDescription")}
                    </p>

                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("SelectWoredas")}
                    </label>
                    <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-3 mb-4 bg-gray-50">
                        {woredas?.length > 0 ? (
                            woredas.map((woreda: any) => {
                                const distributed = isAlreadyDistributed(woreda);
                                return (
                                <div key={woreda.uu_id} className={`flex items-center mb-2 p-2 rounded transition-colors ${distributed ? "opacity-60" : "hover:bg-white"}`}>
                                    <input
                                        type="checkbox"
                                        id={woreda.uu_id}
                                        disabled={distributed}
                                        checked={distributed || selectedWoredas.some((w) => w.uu_id === woreda.uu_id)}
                                        onChange={(e) => {
                                            if (distributed) return;
                                            if (e.target.checked) {
                                                setSelectedWoredas([...selectedWoredas, woreda]);
                                            } else {
                                                setSelectedWoredas(selectedWoredas.filter((w) => w.uu_id !== woreda.uu_id));
                                            }
                                        }}
                                        className="mr-3 w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 disabled:cursor-not-allowed"
                                    />
                                    <label htmlFor={woreda.uu_id} className={`text-sm font-medium ${distributed ? "text-gray-400 cursor-not-allowed" : "text-gray-700 cursor-pointer"}`}>
                                        {woreda.unit_name}
                                        {distributed && (
                                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">
                                                {t("AlreadyDistributed") || "Already distributed"}
                                            </span>
                                        )}
                                    </label>
                                </div>
                                );
                            })
                        ) : (
                            <p className="text-sm text-gray-500 p-2">{t("NoWoredasAvailable")}</p>
                        )}
                    </div>
                    {selectedWoredas.length > 0 && (
                        <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-sm font-semibold text-gray-800 mb-2">
                                {t("SelectedWoredasCount", { count: selectedWoredas.length })}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {selectedWoredas.map((woreda) => (
                                    <span key={woreda.uu_id} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800 border border-green-300 shadow-sm">
                                        {woreda.unit_name}
                                        <button
                                            onClick={() => setSelectedWoredas(selectedWoredas.filter((w) => w.uu_id !== woreda.uu_id))}
                                            className="ml-2 text-green-600 hover:text-green-800 font-bold"
                                            title={t("RemoveWoreda")}
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="mt-6 pb-8 flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4">
                        <button
                            onClick={handleClose}
                            className="flex-1 px-6 py-3 rounded-lg bg-gray-200 text-gray-800 font-semibold hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-colors"
                        >
                            {t("Cancel")}
                        </button>
                        <button
                            onClick={() => setIsConfirmationModalOpen(true)}
                            disabled={selectedWoredas.length === 0}
                            className="flex-1 px-6 py-3 rounded-lg bg-gradient-to-r from-gray-800 to-black text-white font-semibold hover:from-gray-900 hover:to-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                        >
                            {loading ? (
                                <div className="flex items-center justify-center">
                                    {LoadingIcon} {t("Processing")}
                                </div>
                            ) : (
                                t("DistributeToWoredasButton", { count: selectedWoredas.length })
                            )}
                        </button>
                    </div>
                </div>
            </div>
            <ConfirmationModal
                isOpen={isConfirmationModalOpen}
                isLoading={loading}
                onConfirm={() => {
                    handleForward();
                    setIsConfirmationModalOpen(false);
                }}
                onCancel={() => setIsConfirmationModalOpen(false)}
                title={t("ConfirmDistributionToWoredas")}
                message={t("ConfirmDistributionToWoredasMessage", { count: selectedWoredas.length })}
                confirmText={t("Distribute")}
                cancelText={t("Cancel")}
                type="warning"
            />
        </>
    );
}